import type {
  XiangqiColor,
  XiangqiPieceType,
  XiangqiPiece,
  XiangqiMove,
  XiangqiGameState,
} from '../types/xiangqi.ts';

/**
 * Creates the standard 32-piece initial setup of Xiangqi
 */
export function createInitialXiangqiBoard(): XiangqiPiece[] {
  return [
    // --- BLACK PIECES (Row 0 to 3) ---
    { id: 'b_c1', type: 'chariot', color: 'black', row: 0, col: 0 },
    { id: 'b_h1', type: 'horse', color: 'black', row: 0, col: 1 },
    { id: 'b_e1', type: 'elephant', color: 'black', row: 0, col: 2 },
    { id: 'b_a1', type: 'advisor', color: 'black', row: 0, col: 3 },
    { id: 'b_k', type: 'general', color: 'black', row: 0, col: 4 },
    { id: 'b_a2', type: 'advisor', color: 'black', row: 0, col: 5 },
    { id: 'b_e2', type: 'elephant', color: 'black', row: 0, col: 6 },
    { id: 'b_h2', type: 'horse', color: 'black', row: 0, col: 7 },
    { id: 'b_c2', type: 'chariot', color: 'black', row: 0, col: 8 },

    { id: 'b_p1', type: 'cannon', color: 'black', row: 2, col: 1 },
    { id: 'b_p2', type: 'cannon', color: 'black', row: 2, col: 7 },

    { id: 'b_s1', type: 'soldier', color: 'black', row: 3, col: 0 },
    { id: 'b_s2', type: 'soldier', color: 'black', row: 3, col: 2 },
    { id: 'b_s3', type: 'soldier', color: 'black', row: 3, col: 4 },
    { id: 'b_s4', type: 'soldier', color: 'black', row: 3, col: 6 },
    { id: 'b_s5', type: 'soldier', color: 'black', row: 3, col: 8 },

    // --- RED PIECES (Row 6 to 9) ---
    { id: 'r_s1', type: 'soldier', color: 'red', row: 6, col: 0 },
    { id: 'r_s2', type: 'soldier', color: 'red', row: 6, col: 2 },
    { id: 'r_s3', type: 'soldier', color: 'red', row: 6, col: 4 },
    { id: 'r_s4', type: 'soldier', color: 'red', row: 6, col: 6 },
    { id: 'r_s5', type: 'soldier', color: 'red', row: 6, col: 8 },

    { id: 'r_p1', type: 'cannon', color: 'red', row: 7, col: 1 },
    { id: 'r_p2', type: 'cannon', color: 'red', row: 7, col: 7 },

    { id: 'r_c1', type: 'chariot', color: 'red', row: 9, col: 0 },
    { id: 'r_h1', type: 'horse', color: 'red', row: 9, col: 1 },
    { id: 'r_e1', type: 'elephant', color: 'red', row: 9, col: 2 },
    { id: 'r_a1', type: 'advisor', color: 'red', row: 9, col: 3 },
    { id: 'r_k', type: 'general', color: 'red', row: 9, col: 4 },
    { id: 'r_a2', type: 'advisor', color: 'red', row: 9, col: 5 },
    { id: 'r_e2', type: 'elephant', color: 'red', row: 9, col: 6 },
    { id: 'r_h2', type: 'horse', color: 'red', row: 9, col: 7 },
    { id: 'r_c2', type: 'chariot', color: 'red', row: 9, col: 8 },
  ];
}

export function createInitialXiangqiGame(): XiangqiGameState {
  return {
    pieces: createInitialXiangqiBoard(),
    turn: 'red',
    isCheck: false,
    isCheckmate: false,
    isStalemate: false,
    winner: null,
    moveHistory: [],
    lastMove: null,
  };
}

export function getPieceAt(pieces: XiangqiPiece[], row: number, col: number): XiangqiPiece | null {
  return pieces.find((p) => p.row === row && p.col === col) || null;
}

export function isInsidePalace(color: XiangqiColor, row: number, col: number): boolean {
  if (col < 3 || col > 5) return false;
  if (color === 'red') {
    return row >= 7 && row <= 9;
  } else {
    return row >= 0 && row <= 2;
  }
}

/**
 * Checks if two generals face each other along the same column with no pieces in between
 */
export function areGeneralsFacing(pieces: XiangqiPiece[]): boolean {
  const redKing = pieces.find((p) => p.type === 'general' && p.color === 'red');
  const blackKing = pieces.find((p) => p.type === 'general' && p.color === 'black');
  if (!redKing || !blackKing) return false;

  if (redKing.col !== blackKing.col) return false;

  const col = redKing.col;
  const minRow = Math.min(redKing.row, blackKing.row);
  const maxRow = Math.max(redKing.row, blackKing.row);

  for (let r = minRow + 1; r < maxRow; r++) {
    if (getPieceAt(pieces, r, col)) {
      return false; // Intervening piece found
    }
  }

  return true; // No intervening piece -> Flying Generals violation!
}

/**
 * Returns pseudo-legal moves for a piece (ignoring check and flying generals)
 */
export function getPseudoLegalMoves(piece: XiangqiPiece, pieces: XiangqiPiece[]): { toRow: number; toCol: number }[] {
  const moves: { toRow: number; toCol: number }[] = [];
  const { row: r, col: c, color, type } = piece;

  const canMoveTo = (tr: number, tc: number): boolean => {
    if (tr < 0 || tr > 9 || tc < 0 || tc > 8) return false;
    const target = getPieceAt(pieces, tr, tc);
    if (!target) return true;
    return target.color !== color;
  };

  switch (type) {
    case 'general': {
      // 1 step orthogonally within Palace
      const deltas = [[-1, 0], [1, 0], [0, -1], [0, 1]];
      for (const [dr, dc] of deltas) {
        const tr = r + dr;
        const tc = c + dc;
        if (isInsidePalace(color, tr, tc) && canMoveTo(tr, tc)) {
          moves.push({ toRow: tr, toCol: tc });
        }
      }
      break;
    }

    case 'advisor': {
      // 1 step diagonally within Palace
      const deltas = [[-1, -1], [-1, 1], [1, -1], [1, 1]];
      for (const [dr, dc] of deltas) {
        const tr = r + dr;
        const tc = c + dc;
        if (isInsidePalace(color, tr, tc) && canMoveTo(tr, tc)) {
          moves.push({ toRow: tr, toCol: tc });
        }
      }
      break;
    }

    case 'elephant': {
      // 2 steps diagonally, cannot cross river, eye of elephant must be free
      const deltas = [
        [-2, -2, -1, -1],
        [-2, 2, -1, 1],
        [2, -2, 1, -1],
        [2, 2, 1, 1],
      ];
      for (const [dr, dc, er, ec] of deltas) {
        const tr = r + dr;
        const tc = c + dc;
        if (tr < 0 || tr > 9 || tc < 0 || tc > 8) continue;

        // River boundary
        if (color === 'red' && tr < 5) continue;
        if (color === 'black' && tr > 4) continue;

        // Eye of elephant check
        const eye = getPieceAt(pieces, r + er, c + ec);
        if (!eye && canMoveTo(tr, tc)) {
          moves.push({ toRow: tr, toCol: tc });
        }
      }
      break;
    }

    case 'horse': {
      // 8 L-shapes, blocked if horse foot is occupied
      const horseSteps = [
        [-2, -1, -1, 0], [-2, 1, -1, 0], // Up
        [2, -1, 1, 0], [2, 1, 1, 0],     // Down
        [-1, -2, 0, -1], [1, -2, 0, -1], // Left
        [-1, 2, 0, 1], [1, 2, 0, 1],     // Right
      ];
      for (const [dr, dc, fr, fc] of horseSteps) {
        const tr = r + dr;
        const tc = c + dc;
        if (tr < 0 || tr > 9 || tc < 0 || tc > 8) continue;

        // Horse foot check
        const foot = getPieceAt(pieces, r + fr, c + fc);
        if (!foot && canMoveTo(tr, tc)) {
          moves.push({ toRow: tr, toCol: tc });
        }
      }
      break;
    }

    case 'chariot': {
      // 4 orthogonal rays
      const dirs = [[-1, 0], [1, 0], [0, -1], [0, 1]];
      for (const [dr, dc] of dirs) {
        let tr = r + dr;
        let tc = c + dc;
        while (tr >= 0 && tr <= 9 && tc >= 0 && tc <= 8) {
          const target = getPieceAt(pieces, tr, tc);
          if (!target) {
            moves.push({ toRow: tr, toCol: tc });
          } else {
            if (target.color !== color) {
              moves.push({ toRow: tr, toCol: tc });
            }
            break;
          }
          tr += dr;
          tc += dc;
        }
      }
      break;
    }

    case 'cannon': {
      // Moves like chariot when not capturing; needs exactly 1 screen to capture
      const dirs = [[-1, 0], [1, 0], [0, -1], [0, 1]];
      for (const [dr, dc] of dirs) {
        let tr = r + dr;
        let tc = c + dc;
        let foundPlatform = false;

        while (tr >= 0 && tr <= 9 && tc >= 0 && tc <= 8) {
          const target = getPieceAt(pieces, tr, tc);
          if (!foundPlatform) {
            if (!target) {
              moves.push({ toRow: tr, toCol: tc });
            } else {
              foundPlatform = true; // First piece is the platform (ngòi)
            }
          } else {
            // Already found platform, looking for capture target
            if (target) {
              if (target.color !== color) {
                moves.push({ toRow: tr, toCol: tc });
              }
              break; // Cannon can only jump 1 piece
            }
          }
          tr += dr;
          tc += dc;
        }
      }
      break;
    }

    case 'soldier': {
      const isRed = color === 'red';
      const forward = isRed ? -1 : 1;
      const tr = r + forward;

      // Always forward 1 step
      if (canMoveTo(tr, c)) {
        moves.push({ toRow: tr, toCol: c });
      }

      // If crossed river, can also move sideways
      const hasCrossed = isRed ? r <= 4 : r >= 5;
      if (hasCrossed) {
        if (canMoveTo(r, c - 1)) moves.push({ toRow: r, toCol: c - 1 });
        if (canMoveTo(r, c + 1)) moves.push({ toRow: r, toCol: c + 1 });
      }
      break;
    }
  }

  return moves;
}

/**
 * Checks if kingColor's general is attacked by any opposing piece
 */
export function isXiangqiKingInCheck(pieces: XiangqiPiece[], kingColor: XiangqiColor): boolean {
  const king = pieces.find((p) => p.type === 'general' && p.color === kingColor);
  if (!king) return true; // King missing -> counts as check/lost

  const oppColor: XiangqiColor = kingColor === 'red' ? 'black' : 'red';
  const oppPieces = pieces.filter((p) => p.color === oppColor);

  for (const opp of oppPieces) {
    const pseudoMoves = getPseudoLegalMoves(opp, pieces);
    if (pseudoMoves.some((m) => m.toRow === king.row && m.toCol === king.col)) {
      return true;
    }
  }

  return false;
}

/**
 * Simulates move and verifies it is legal (doesn't cause check to own king & no flying generals)
 */
export function isMoveLegal(
  piece: XiangqiPiece,
  toRow: number,
  toCol: number,
  pieces: XiangqiPiece[]
): boolean {
  // Apply move on copy
  const nextPieces = pieces
    .filter((p) => !(p.row === toRow && p.col === toCol))
    .map((p) => (p.id === piece.id ? { ...p, row: toRow, col: toCol } : p));

  // 1. Cannot have Flying Generals
  if (areGeneralsFacing(nextPieces)) return false;

  // 2. Cannot leave own king in check
  if (isXiangqiKingInCheck(nextPieces, piece.color)) return false;

  return true;
}

/**
 * Returns all fully validated legal moves for a given piece
 */
export function getXiangqiLegalMovesForPiece(
  piece: XiangqiPiece,
  pieces: XiangqiPiece[]
): { toRow: number; toCol: number }[] {
  const pseudoMoves = getPseudoLegalMoves(piece, pieces);
  return pseudoMoves.filter((m) => isMoveLegal(piece, m.toRow, m.toCol, pieces));
}

/**
 * Returns all legal moves for a player of color
 */
export function getAllXiangqiLegalMoves(
  color: XiangqiColor,
  pieces: XiangqiPiece[]
): { piece: XiangqiPiece; toRow: number; toCol: number }[] {
  const result: { piece: XiangqiPiece; toRow: number; toCol: number }[] = [];
  const myPieces = pieces.filter((p) => p.color === color);

  for (const p of myPieces) {
    const validDestinations = getXiangqiLegalMovesForPiece(p, pieces);
    for (const d of validDestinations) {
      result.push({ piece: p, toRow: d.toRow, toCol: d.toCol });
    }
  }

  return result;
}

/**
 * Creates human-readable Vietnamese notation for Xiangqi
 */
export function formatNotationVi(
  piece: XiangqiPiece,
  fromRow: number,
  fromCol: number,
  toRow: number,
  toCol: number
): string {
  const names: Record<XiangqiPieceType, { red: string; black: string }> = {
    general: { red: 'Tướng', black: 'Tướng' },
    advisor: { red: 'Sĩ', black: 'Sĩ' },
    elephant: { red: 'Tượng', black: 'Tượng' },
    horse: { red: 'Mã', black: 'Mã' },
    chariot: { red: 'Xe', black: 'Xe' },
    cannon: { red: 'Pháo', black: 'Pháo' },
    soldier: { red: 'Tốt', black: 'Tốt' },
  };

  const name = piece.color === 'red' ? names[piece.type].red : names[piece.type].black;
  const isRed = piece.color === 'red';

  // Columns in Xiangqi: Red counts 1..9 from right to left (col 8 -> 1, col 0 -> 9)
  // Black counts 1..9 from right to left from Black's perspective (col 0 -> 1, col 8 -> 9)
  const cFrom = isRed ? 9 - fromCol : fromCol + 1;
  const cTo = isRed ? 9 - toCol : toCol + 1;

  if (fromRow === toRow) {
    return `${name} ${cFrom} bình ${cTo}`;
  }

  const isAdvancing = isRed ? toRow < fromRow : toRow > fromRow;
  const action = isAdvancing ? 'tấn' : 'thoái';

  if (piece.type === 'chariot' || piece.type === 'cannon' || piece.type === 'soldier' || piece.type === 'general') {
    if (fromCol === toCol) {
      const steps = Math.abs(toRow - fromRow);
      return `${name} ${cFrom} ${action} ${steps}`;
    }
  }

  return `${name} ${cFrom} ${action} ${cTo}`;
}

/**
 * Applies a move and updates game state
 */
export function applyXiangqiMove(
  state: XiangqiGameState,
  fromRow: number,
  fromCol: number,
  toRow: number,
  toCol: number
): XiangqiGameState {
  const piece = getPieceAt(state.pieces, fromRow, fromCol);
  if (!piece || piece.color !== state.turn) return state;

  const legalMoves = getXiangqiLegalMovesForPiece(piece, state.pieces);
  const isValid = legalMoves.some((m) => m.toRow === toRow && m.toCol === toCol);
  if (!isValid) return state;

  const captured = getPieceAt(state.pieces, toRow, toCol) || undefined;
  const notation = formatNotationVi(piece, fromRow, fromCol, toRow, toCol);

  const move: XiangqiMove = {
    fromRow,
    fromCol,
    toRow,
    toCol,
    piece: { ...piece },
    captured,
    notationVi: notation,
  };

  // Update pieces
  const nextPieces = state.pieces
    .filter((p) => !(p.row === toRow && p.col === toCol))
    .map((p) => (p.id === piece.id ? { ...p, row: toRow, col: toCol } : p));

  const nextTurn: XiangqiColor = state.turn === 'red' ? 'black' : 'red';
  const isCheck = isXiangqiKingInCheck(nextPieces, nextTurn);
  const nextLegalMoves = getAllXiangqiLegalMoves(nextTurn, nextPieces);

  let isCheckmate = false;
  let isStalemate = false;
  let winner = state.winner;

  if (nextLegalMoves.length === 0) {
    if (isCheck) {
      isCheckmate = true;
      winner = state.turn; // Attacker wins by checkmate
    } else {
      isStalemate = true;
      winner = state.turn; // In Xiangqi, stalemate is a LOSS for the immobilized player
    }
  }

  return {
    pieces: nextPieces,
    turn: nextTurn,
    isCheck,
    isCheckmate,
    isStalemate,
    winner,
    moveHistory: [move, ...state.moveHistory],
    lastMove: move,
  };
}

/**
 * Evaluates board score from Red's perspective
 */
const PIECE_VALUES: Record<XiangqiPieceType, number> = {
  general: 10000,
  chariot: 950,
  cannon: 480,
  horse: 420,
  advisor: 200,
  elephant: 200,
  soldier: 120,
};

export function evaluateXiangqiBoard(pieces: XiangqiPiece[]): number {
  let score = 0;

  for (const p of pieces) {
    let val = PIECE_VALUES[p.type];

    // Positional bonuses
    if (p.type === 'soldier') {
      const isRed = p.color === 'red';
      const crossed = isRed ? p.row <= 4 : p.row >= 5;
      if (crossed) val += 100;
      if ((isRed && p.row <= 2) || (!isRed && p.row >= 7)) val += 50; // Close to palace
    } else if (p.type === 'horse') {
      // Horse in center is stronger
      if (p.col >= 2 && p.col <= 6) val += 30;
    } else if (p.type === 'cannon') {
      // Cannon on central files is strong
      if (p.col === 4) val += 35;
    }

    if (p.color === 'red') {
      score += val;
    } else {
      score -= val;
    }
  }

  return score;
}

/**
 * Basic Minimax AI with Alpha-Beta pruning for Xiangqi
 */
export function getBestAIMove(
  state: XiangqiGameState,
  depth: number = 2
): { fromRow: number; fromCol: number; toRow: number; toCol: number } | null {
  const legalMoves = getAllXiangqiLegalMoves(state.turn, state.pieces);
  if (legalMoves.length === 0) return null;

  const isMaximizing = state.turn === 'red';
  let bestScore = isMaximizing ? -Infinity : Infinity;
  let bestMove: { fromRow: number; fromCol: number; toRow: number; toCol: number } | null = null;

  // Shuffle slightly so AI doesn't always play identical moves
  const sortedMoves = [...legalMoves].sort(() => Math.random() - 0.5);

  for (const m of sortedMoves) {
    // Quick evaluate simulated move
    const simulated = applyXiangqiMove(state, m.piece.row, m.piece.col, m.toRow, m.toCol);
    const score = minimax(simulated, depth - 1, -Infinity, Infinity, !isMaximizing);

    if (isMaximizing) {
      if (score > bestScore) {
        bestScore = score;
        bestMove = { fromRow: m.piece.row, fromCol: m.piece.col, toRow: m.toRow, toCol: m.toCol };
      }
    } else {
      if (score < bestScore) {
        bestScore = score;
        bestMove = { fromRow: m.piece.row, fromCol: m.piece.col, toRow: m.toRow, toCol: m.toCol };
      }
    }
  }

  return bestMove || {
    fromRow: sortedMoves[0].piece.row,
    fromCol: sortedMoves[0].piece.col,
    toRow: sortedMoves[0].toRow,
    toCol: sortedMoves[0].toCol,
  };
}

function minimax(
  state: XiangqiGameState,
  depth: number,
  alpha: number,
  beta: number,
  isMaximizing: boolean
): number {
  if (depth === 0 || state.winner !== null) {
    return evaluateXiangqiBoard(state.pieces);
  }

  const moves = getAllXiangqiLegalMoves(state.turn, state.pieces);
  if (moves.length === 0) {
    return evaluateXiangqiBoard(state.pieces);
  }

  if (isMaximizing) {
    let maxEval = -Infinity;
    for (const m of moves) {
      const nextState = applyXiangqiMove(state, m.piece.row, m.piece.col, m.toRow, m.toCol);
      const evalScore = minimax(nextState, depth - 1, alpha, beta, false);
      maxEval = Math.max(maxEval, evalScore);
      alpha = Math.max(alpha, evalScore);
      if (beta <= alpha) break;
    }
    return maxEval;
  } else {
    let minEval = Infinity;
    for (const m of moves) {
      const nextState = applyXiangqiMove(state, m.piece.row, m.piece.col, m.toRow, m.toCol);
      const evalScore = minimax(nextState, depth - 1, alpha, beta, true);
      minEval = Math.min(minEval, evalScore);
      beta = Math.min(beta, evalScore);
      if (beta <= alpha) break;
    }
    return minEval;
  }
}
